import React, { useEffect, useMemo, useRef, useState } from 'react';

import clsx from 'clsx';

import { times } from 'lodash';

import { useShouldScroll } from 'lib/hooks';
import searchClient, {
  FacetLabelKey,
  FacetsList,
  SearchFilterProps,
  SearchOptions,
  getDefaultFacetLabel,
  getFacetValue,
} from 'lib/search';

const COLS_NUMBER_DEFAULT = 2;

export default function SearchFilters(props: SearchFilterProps) {
  const client = useMemo(
    () => props.client || searchClient(props),
    [
      props.client,
      props.algoliaAppId,
      props.algoliaApiKey,
      props.algoliaIndexName,
    ]
  );

  const [isClientInitialized, setIsClientInitialized] = useState(
    !!props.isClientInitialized
  );
  const [facets, setFacets] = useState<FacetsList | undefined>(props.facets);
  const [isLoading, setIsLoading] = useState(!props.isClientInitialized);
  const { columns = COLS_NUMBER_DEFAULT } = props;

  useEffect(() => {
    if (!isClientInitialized && !props.client) {
      client.validateIndex().then(() => setIsClientInitialized(true));
    }
  }, []);

  useEffect(() => {
    setIsLoading(!!props.isLoading);
  }, [props.isLoading]);

  useEffect(() => {
    if (!props.facets) return;
    setFacets(props.facets);
  }, [props.facets]);

  // Load available facets on initialization and when facetQuery
  // value is updated
  useEffect(() => {
    async function loadFacets() {
      const searchOptions = {
        readerCompaniesFilter: props.readerCompanyId,
        driversOnly: props.driversOnly,
        includeDeleted: props.includeDeleted,
        searchFilters: props.updateFiltersOnSelect ? props.selectedFilters : [],
      };
      setIsLoading(true);
      const facets = await client.getAllFacets(
        searchOptions,
        props.facetsQuery
      );
      setFacets(facets);
      setIsLoading(false);
    }
    if (isClientInitialized && !props.facets) {
      loadFacets();
    }
  }, [isClientInitialized, props.facetsQuery]);

  // Update available facets when selectedFilters are updated
  // and updateFiltersOnSelect option is active
  useEffect(() => {
    async function loadFacets() {
      const searchOptions: SearchOptions<any> = {
        readerCompaniesFilter: props.readerCompanyId,
        driversOnly: props.driversOnly,
        includeDeleted: props.includeDeleted,
        searchFilters: props.selectedFilters,
      };
      setIsLoading(true);
      const facets = await client.getAllFacets(
        searchOptions,
        props.facetsQuery
      );
      setFacets(facets);
      setIsLoading(false);
    }
    if (isClientInitialized && props.updateFiltersOnSelect && !props.facets) {
      loadFacets();
    }
  }, [
    isClientInitialized,
    props.selectedFilters?.length,
    props.updateFiltersOnSelect,
  ]);

  const onCheckboxChange =
    (filterId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!props.onSelectFilter) return;
      props.onSelectFilter(filterId, event.target.checked);
    };

  if (!facets && !isLoading) return null;

  const {
    facetsList = Object.keys(facets || {}),
    facetAvailableValues,
    facetsLabels = {},
    selectedFilters,
    mandatoryAttributes,
    onSelectMandatoryAttribute,
    mandatoryCheckboxLabel,
    unformattedAttributes,
    facetValueFormatter,
  } = props;
  return (
    <div className="isd-search-tw">
      <div
        className={`isd-search-filters-grid grid grid-cols-${columns} gap-4`}
      >
        {facetsList.map((facetAttributeStr) => {
          const facetAttribute = facetAttributeStr as FacetLabelKey;
          return (
            <SearchFilterAttribute
              key={facetAttribute}
              facets={facets || {}}
              facetAttribute={facetAttribute}
              facetsLabels={facetsLabels}
              facetAvailableValues={facetAvailableValues}
              onCheckboxChange={onCheckboxChange}
              selectedFilters={selectedFilters}
              mandatoryAttributes={mandatoryAttributes}
              onSelectMandatoryAttribute={onSelectMandatoryAttribute}
              mandatoryCheckboxLabel={mandatoryCheckboxLabel}
              unformattedAttributes={unformattedAttributes}
              facetValueFormatter={facetValueFormatter}
              isLoading={isLoading}
            />
          );
        })}
      </div>
      {!!props.onClearFilters && (
        <ClearFilterButton onClick={props.onClearFilters} />
      )}
    </div>
  );
}

const DEFAULT_LOADING_ELEMENTS_COUNT = 5;

function SearchFilterAttribute({
  facets,
  facetAttribute,
  facetsLabels = {},
  facetAvailableValues,
  onCheckboxChange,
  selectedFilters,
  mandatoryAttributes,
  onSelectMandatoryAttribute,
  mandatoryCheckboxLabel,
  unformattedAttributes,
  facetValueFormatter,
  isLoading,
}: {
  facets: FacetsList;
  facetAttribute: FacetLabelKey;
  onCheckboxChange: (
    checkboxId: string
  ) => React.ChangeEventHandler<HTMLInputElement>;
  isLoading?: boolean;
} & Pick<
  SearchFilterProps,
  | 'facetsLabels'
  | 'facetAvailableValues'
  | 'selectedFilters'
  | 'mandatoryAttributes'
  | 'onSelectMandatoryAttribute'
  | 'mandatoryCheckboxLabel'
  | 'unformattedAttributes'
  | 'facetValueFormatter'
>) {
  const searchFilterAttributeContainer = useRef<HTMLDivElement | null>(null);
  const shouldScrollContainer = useShouldScroll(searchFilterAttributeContainer);

  const getIsFilterSelected = (filterId: string) =>
    !!selectedFilters ? selectedFilters.includes(filterId) : undefined;

  const facetValuesWithQty = facets[facetAttribute] || {};
  let facetValues =
    facetAvailableValues?.[facetAttribute] || Object.keys(facetValuesWithQty);

  // If facets are still loading and there are no available values, generate dummy values to display
  if (!facetValues.length) {
    if (isLoading) {
      facetValues = times(
        DEFAULT_LOADING_ELEMENTS_COUNT,
        (i) => `loading-facet-${i}`
      );
    } else {
      return null;
    }
  }

  const facetLabel =
    facetsLabels[facetAttribute] || getDefaultFacetLabel(facetAttribute);
  return (
    <div className="attribute-cell lg:min-w-[175px]">
      <div className="mb-2 flex items-center justify-between">
        <div className="search-filter-attribute-title font-black text-black">
          {facetLabel}
        </div>
        {!!onSelectMandatoryAttribute && (
          <MandatoryFilterCheckbox
            facetAttribute={facetAttribute}
            mandatoryAttributes={mandatoryAttributes}
            onSelectMandatoryAttribute={onSelectMandatoryAttribute}
            mandatoryCheckboxLabel={mandatoryCheckboxLabel}
          />
        )}
      </div>
      <div
        ref={searchFilterAttributeContainer}
        className={clsx(
          'isd-search-filter-values-list max-h-96',
          (shouldScrollContainer || facetValues.length > 6) &&
            'overflow-y-scroll'
        )}
      >
        {facetValues.map((facetValue) => {
          const checkboxId = `${facetAttribute}:"${facetValue}"`;
          return (
            <div key={facetValue} className="flex pb-1">
              <input
                type="checkbox"
                className="search-filter-checkbox mt-0!"
                id={checkboxId}
                onChange={onCheckboxChange(checkboxId)}
                checked={getIsFilterSelected(checkboxId)}
              />
              {
                // While loading, display a skeleton instead of a label
                isLoading ? (
                  <div className="h-5 w-full animate-pulse rounded-xs bg-gray-300 px-3"></div>
                ) : (
                  <label
                    className="search-filter-label mb-0 inline-block pl-1 text-sm text-neutral-700 hover:cursor-pointer"
                    htmlFor={checkboxId}
                  >
                    {unformattedAttributes?.includes(facetAttribute)
                      ? facetValue
                      : getFacetValue(
                          facetAttribute,
                          facetValue,
                          facetValueFormatter
                        )}
                  </label>
                )
              }
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MandatoryFilterCheckbox({
  facetAttribute,
  mandatoryAttributes = [],
  onSelectMandatoryAttribute,
  mandatoryCheckboxLabel = 'Show Only',
}: {
  facetAttribute: FacetLabelKey;
} & Pick<
  SearchFilterProps,
  | 'mandatoryAttributes'
  | 'onSelectMandatoryAttribute'
  | 'mandatoryCheckboxLabel'
>) {
  const getIsAttributeMandatory = (facetAttribute: FacetLabelKey) => {
    return mandatoryAttributes.includes(facetAttribute);
  };

  const onCheckboxChange = (facetAttribute: FacetLabelKey) => {
    if (!onSelectMandatoryAttribute) return;
    else
      return (event: React.ChangeEvent<HTMLInputElement>) =>
        onSelectMandatoryAttribute(facetAttribute, event.target.checked);
  };

  return (
    <div className="search-filter-mandatory-checkbox-container mr-5 flex items-center">
      <input
        type="checkbox"
        className="search-filter-mandatory-checkbox mt-0!"
        id={`is-mandatory-checkbox-${facetAttribute}`}
        onChange={onCheckboxChange(facetAttribute)}
        checked={getIsAttributeMandatory(facetAttribute)}
      />
      <label
        className="search-filter-mandatory-label mb-0 inline-block pl-1 text-xs text-neutral-700 hover:cursor-pointer"
        htmlFor={`is-mandatory-checkbox-${facetAttribute}`}
      >
        {mandatoryCheckboxLabel}
      </label>
    </div>
  );
}

function ClearFilterButton({
  onClick,
}: {
  onClick: React.MouseEventHandler<HTMLDivElement>;
}) {
  return (
    <div className="flex justify-end py-2">
      <div
        onClick={onClick}
        className="clear-filters-button clickable rounded-full px-2 py-1 text-[0.6rem] font-bold select-none hover:bg-zinc-50 focus:bg-zinc-50 active:bg-zinc-200"
      >
        Clear Filters
      </div>
    </div>
  );
}
